/**
 * Design-system primitives.
 *
 * These are the pieces every screen is assembled from. They are deliberately
 * few: a console that mixes six kinds of card and four kinds of button reads as
 * assembled rather than designed. Each primitive takes its meaning from a
 * semantic prop (`tone`, `state`) rather than from a colour the caller picks, so
 * a status colour cannot drift between screens.
 */

import type { ReactNode, ButtonHTMLAttributes, HTMLAttributes } from 'react'
import { useId } from 'react'
import clsx from 'clsx'
import './primitives.css'

/* ------------------------------------------------------------------ Panel */

// `title` is widened from the DOM's string-only tooltip attribute to ReactNode,
// because a panel heading routinely carries a badge or a count beside its text.
interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode
  /** Right-aligned controls in the panel header. */
  actions?: ReactNode
  /** A figure beside the title — a count, a worst-case percentage, a severity
   *  dot. It sits inside the disclosure button when there is one, so folding a
   *  section away never folds away what it was telling you. */
  summary?: ReactNode
  /** Removes the body padding, for panels holding a table or a terminal. */
  flush?: boolean
  /** Draws the corner-bracket frame — the instrument treatment for a card that
   *  stands on its own (an account, a host), as opposed to a plain section. */
  framed?: boolean
  /** Whether the body is shown. Only meaningful with `onToggle`. */
  open?: boolean
  /** Makes the header a disclosure button. The caller owns the state, because
   *  what an operator folded away has to outlive the component. */
  onToggle?: () => void
  children: ReactNode
}

/**
 * A titled surface. The base unit every screen is built from.
 *
 * `onToggle` turns the header into a disclosure. The body is hidden rather than
 * unmounted so a folded section costs nothing to reopen and keeps whatever
 * scroll position it had — and the summary stays in the header either way, so a
 * section that is folded away can still say a person is waiting.
 *
 * `actions` stay outside the button: a control nested inside another control is
 * invalid markup, and a refresh that also toggled the panel would be a surprise
 * every time it was pressed.
 */
export function Panel({
  title,
  actions,
  summary,
  flush,
  framed,
  open = true,
  onToggle,
  children,
  className,
  ...rest
}: PanelProps) {
  const bodyId = useId()
  return (
    <section
      className={clsx(
        'k-panel',
        framed && 'k-panel--framed',
        onToggle && !open && 'k-panel--folded',
        className,
      )}
      {...rest}
    >
      {(title || actions || summary) && (
        <header className="k-panel__head">
          {onToggle ? (
            <h2 className="k-panel__title k-panel__title--fold">
              <button
                type="button"
                className="k-panel__toggle"
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={onToggle}
              >
                <span className={clsx('k-panel__chev', open && 'k-panel__chev--open')} aria-hidden="true" />
                <span className="k-panel__label">{title}</span>
                {summary}
              </button>
            </h2>
          ) : (
            <>
              {typeof title === 'string' ? <h2 className="k-panel__title">{title}</h2> : title}
              {summary}
            </>
          )}
          {actions && <div className="k-panel__actions">{actions}</div>}
        </header>
      )}
      <div
        id={bodyId}
        hidden={onToggle ? !open : undefined}
        className={clsx('k-panel__body', flush && 'k-panel__body--flush')}
      >
        {children}
      </div>
    </section>
  )
}

/* ----------------------------------------------------------------- Button */

type ButtonVariant = 'primary' | 'default' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: 'sm' | 'md'
  /** Renders a busy state and disables interaction. */
  busy?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'default',
  size = 'md',
  busy,
  icon,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={clsx('k-btn', `k-btn--${variant}`, `k-btn--${size}`, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <Spinner size={12} /> : icon}
      {children}
    </button>
  )
}

/* -------------------------------------------------------------------- Tone */

/**
 * The semantic tones every status-bearing component shares.
 *
 * `unknown` is a first-class tone rather than a shade of neutral. Kimene refuses
 * to render an unmeasured value as though it were fine, and the interface has to
 * carry that refusal — a grey badge reads as "nothing to report", which is the
 * opposite of what UNKNOWN means here.
 */
export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'err' | 'info' | 'unknown'

/* ------------------------------------------------------------------ Badge */

interface BadgeProps {
  tone?: Tone
  /** Renders as an outline rather than a filled wash. */
  subtle?: boolean
  /** Uses the mono face, for machine values such as ids and tokens. */
  mono?: boolean
  title?: string
  children: ReactNode
}

export function Badge({ tone = 'neutral', subtle, mono, title, children }: BadgeProps) {
  return (
    <span
      className={clsx('k-badge', `k-badge--${tone}`, subtle && 'k-badge--subtle', mono && 'k-mono')}
      title={title}
    >
      {children}
    </span>
  )
}

/* --------------------------------------------------------------- StatusDot */

interface StatusDotProps {
  tone: Tone
  /** Adds a slow pulse. Reserved for things that are actively happening. */
  live?: boolean
  label?: string
}

/** A small state indicator. Always paired with text — never colour alone. */
export function StatusDot({ tone, live, label }: StatusDotProps) {
  return (
    <span
      className={clsx('k-dot', `k-dot--${tone}`, live && 'k-dot--live')}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  )
}

/* ---------------------------------------------------------------- Spinner */

export function Spinner({ size = 16, label }: { size?: number; label?: string }) {
  return (
    <span
      className="k-spinner"
      style={{ width: size, height: size }}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  )
}

/* -------------------------------------------------------------- EmptyState */

interface EmptyStateProps {
  title: string
  /** What to do next. An empty screen is an invitation to act, not a dead end. */
  hint?: string
  action?: ReactNode
  icon?: ReactNode
}

export function EmptyState({ title, hint, action, icon }: EmptyStateProps) {
  return (
    <div className="k-empty">
      {icon && <div className="k-empty__icon">{icon}</div>}
      <p className="k-empty__title">{title}</p>
      {hint && <p className="k-empty__hint">{hint}</p>}
      {action && <div className="k-empty__action">{action}</div>}
    </div>
  )
}

/* -------------------------------------------------------------- ErrorState */

interface ErrorStateProps {
  title: string
  /** Says what to do about it. Errors never merely apologise. */
  hint?: string
  onRetry?: () => void
  retryLabel?: string
}

export function ErrorState({ title, hint, onRetry, retryLabel = 'Retry' }: ErrorStateProps) {
  return (
    <div className="k-empty k-empty--error" role="alert">
      <p className="k-empty__title">{title}</p>
      {hint && <p className="k-empty__hint">{hint}</p>}
      {onRetry && (
        <div className="k-empty__action">
          <Button onClick={onRetry}>{retryLabel}</Button>
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- Skeleton */

/** Loading placeholder. Sized by the caller so it matches the content it
 *  replaces, which stops the layout jumping when data arrives. */
export function Skeleton({ width, height = 16 }: { width?: number | string; height?: number }) {
  return <span className="k-skeleton" style={{ width: width ?? '100%', height }} aria-hidden />
}

/* ------------------------------------------------------------------- Meter */

interface MeterProps {
  /** 0..1, or null when the value is genuinely unknown. */
  value: number | null | undefined
  tone?: Tone
  /** Shown in place of the bar when value is null. */
  unknownLabel?: string
  ariaLabel: string
}

/**
 * A horizontal fill bar.
 *
 * A null value renders a hatched "unmeasured" track rather than an empty bar,
 * because an empty bar and a zero bar look identical and mean opposite things.
 */
export function Meter({ value, tone = 'accent', unknownLabel, ariaLabel }: MeterProps) {
  if (value == null || Number.isNaN(value)) {
    return (
      <div className="k-meter k-meter--unknown" role="img" aria-label={`${ariaLabel}: unknown`}>
        <span className="k-meter__unknown-label">{unknownLabel ?? '?'}</span>
      </div>
    )
  }
  const pct = Math.max(0, Math.min(1, value)) * 100
  return (
    <div
      className="k-meter"
      role="meter"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={clsx('k-meter__fill', `k-meter__fill--${tone}`)}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

/* -------------------------------------------------------------------- Page */

/**
 * The two-region page frame.
 *
 * The shell's content area is fixed to the viewport height, so a page is a fixed
 * header strip over a body that fills the rest and scrolls on its own. Nothing
 * ever scrolls *behind* the header, which is why the header carries no
 * background: the table moves inside `k-page__body`, not under the title. Every
 * shell route wraps its content in this so "header stays, body scrolls, body
 * fits the screen" holds without each page re-inventing it.
 */
export function Page({ header, children }: { header?: ReactNode; children: ReactNode }) {
  return (
    <div className="k-page">
      {header && <div className="k-page__head">{header}</div>}
      {/* The body is every page's one scrolling region, so it takes a tab stop.
          A screen whose content happens to be entirely read-only — the model
          catalogue is one — otherwise scrolls with the pointer and cannot be
          scrolled by keyboard at all. Set here rather than on the one screen
          that failed the audit, because the next read-only screen would have
          the same defect and no reason to know it. */}
      <div className="k-page__body" tabIndex={0}>
        {children}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- PageHeader */

interface PageHeaderProps {
  title: string
  subtitle?: string
  actions?: ReactNode
  /** Rendered under the title row: filters, tabs. Part of the fixed header. */
  children?: ReactNode
}

/**
 * The header strip: one compact line of title + count + actions, then optional
 * filters. Deliberately minimal — a small stencilled title, not a hero — because
 * the information below it is the point, and every pixel the chrome takes is a
 * row of data the operator cannot see.
 */
export function PageHeader({ title, subtitle, actions, children }: PageHeaderProps) {
  return (
    <header className="k-pagehead">
      <div className="k-pagehead__row">
        <div className="k-pagehead__lead">
          <h1 className="k-pagehead__title">{title}</h1>
          {subtitle && <span className="k-pagehead__sub">{subtitle}</span>}
        </div>
        {actions && <div className="k-pagehead__actions">{actions}</div>}
      </div>
      {children}
    </header>
  )
}

/* -------------------------------------------------------------------- Grid */

/** Responsive auto-fit grid. `min` is the narrowest a cell may become before
 *  the grid drops a column — which is how the console adapts from 4K to a
 *  laptop without a single media query per screen. */
export function Grid({
  min = 280,
  gap = 'var(--k-space-4)',
  fill,
  as: As = 'div',
  ariaLabel,
  children,
  className,
}: {
  min?: number
  gap?: string
  /** `auto-fill` rather than `auto-fit`.
   *
   *  The difference only shows when there are fewer items than tracks, and then
   *  it is the whole thing: `auto-fit` collapses the empty tracks, so a lone card
   *  stretches to the full width of the container — 2500px of one card on a wall
   *  display, which is the shape this grid exists to stop. `fill` keeps the
   *  tracks, so one card is card-sized and the rest of the row is empty. */
  fill?: boolean
  /** A list of things is a list. The default `div` is right for a set of
   *  unrelated panels; a collection of peers should reach a screen reader with a
   *  count and an item position, which only the list elements give it. */
  as?: 'div' | 'ul' | 'ol'
  ariaLabel?: string
  children: ReactNode
  className?: string
}) {
  return (
    <As
      className={clsx('k-grid', className)}
      aria-label={ariaLabel}
      style={{
        gridTemplateColumns: `repeat(${fill ? 'auto-fill' : 'auto-fit'}, minmax(min(${min}px, 100%), 1fr))`,
        gap,
      }}
    >
      {children}
    </As>
  )
}

/* ---------------------------------------------------------------- KeyValue */

/** A definition list for detail panels. Keys are labels, values are content —
 *  and machine values get the mono treatment via the `mono` flag.
 *
 *  `hint` is a sentence under the value, for a term whose name does not explain
 *  itself. It is a line rather than a `title` attribute on purpose: a tooltip is
 *  reachable by neither a touch screen nor a keyboard, so a fact only available
 *  on hover is a fact most operators never see. */
export function KeyValue({
  items,
}: {
  items: Array<{ key: string; label: string; value: ReactNode; mono?: boolean; hint?: string }>
}) {
  return (
    <dl className="k-kv">
      {items.map((it) => (
        <div className="k-kv__row" key={it.key}>
          <dt className="k-kv__key">{it.label}</dt>
          <dd className={clsx('k-kv__val', it.mono && 'k-mono')}>
            {it.value}
            {it.hint && <span className="k-kv__hint">{it.hint}</span>}
          </dd>
        </div>
      ))}
    </dl>
  )
}
